/**
 * InsurerMultiSelect — sélecteur multi-assureurs branché sur le registre
 * dynamique du tenant (9.V.2c-2b).
 *
 * Remplace l'usage de `MultiSelect<InsurerCode>` alimenté par la constante
 * statique `INSURERS`. Composant AUTONOME (ne wrappe pas `MultiSelect`) : la
 * contrainte E1-A « ne pas modifier MultiSelect.tsx » + l'invariant R1
 * « libellé lisible du registre pour un code inactif » sont incompatibles avec
 * le primitif générique (qui lie l'étiquette de la puce à la liste déroulante).
 * On réutilise les mêmes classes de puce pour la parité visuelle, sans nouveau
 * style global.
 *
 * Invariants (9.V.2c-2b) :
 *   - seuls les assureurs ACTIFS sont proposés à l'ajout (nouvelle opération) ;
 *   - une valeur déjà présente mais inactive/inconnue reste visible, retirable,
 *     et NON réajoutable après retrait (absente de la liste déroulante) ;
 *   - aucun filtre implicite ne supprime une valeur au chargement/rerender ;
 *   - un code inactif est distingué par le libellé texte « Nom (inactif) »
 *     (lecteur d'écran compris), fallback code brut si nom indisponible.
 *
 * États : chargement / erreur+réessai / registre vide — toujours dédiés, jamais
 * un picker vide muet. Les valeurs historiques restent affichées dans tous ces
 * états (elles ne sont perdues que si l'utilisateur les retire).
 */

import { useTranslation } from "react-i18next";

import { useInsurerCatalog } from "../lib/queries/insurers";

type Props = {
  /** Codes sélectionnés (peuvent inclure des codes inactifs/historiques). */
  value: readonly string[];
  onChange: (next: string[]) => void;
  /** ID DOM pour le label parent (a11y). Porté par le `<select>`. */
  id?: string;
  placeholder?: string;
  /** Désactive l'ajout (les puces restent retirables). */
  disabled?: boolean;
};

export function InsurerMultiSelect({ value, onChange, id, placeholder, disabled }: Props) {
  const { t } = useTranslation();
  const catalog = useInsurerCatalog();
  const placeholderText = placeholder ?? t("common.select_placeholder");

  // Choix ajoutables = actifs non déjà sélectionnés. Un code inactif présent
  // dans `value` n'apparaît jamais ici → non réajoutable une fois retiré.
  const remaining = catalog.activeOptions.filter((o) => !value.includes(o.value));

  const onAdd = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    e.target.value = "";
  };

  const onRemove = (code: string) => {
    onChange(value.filter((x) => x !== code));
  };

  // Registre vide ET rien de sélectionné → notice dédiée (aucun select muet).
  // Si des valeurs historiques existent, on affiche quand même les puces.
  if (catalog.isEmpty && value.length === 0) {
    return (
      <p className="mt-1 text-sm text-slate-500" role="status">
        {t("common.insurers.empty")}
      </p>
    );
  }

  const selectDisabled = disabled || catalog.isLoading || catalog.isError || remaining.length === 0;

  return (
    <div>
      {catalog.isLoading && (
        <p className="mb-1 text-xs text-slate-500" aria-live="polite">
          {t("common.insurers.loading")}
        </p>
      )}
      {catalog.isError && (
        <div className="mb-1 flex items-center gap-2" role="alert">
          <span className="text-xs text-red-700">{t("common.insurers.error")}</span>
          <button
            type="button"
            onClick={catalog.refetch}
            className="text-brand-700 hover:text-brand-800 focus-visible:ring-brand-500 rounded text-xs font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          >
            {t("common.retry")}
          </button>
        </div>
      )}

      <select
        id={id}
        className="input"
        onChange={onAdd}
        defaultValue=""
        disabled={selectDisabled}
        aria-label={placeholderText}
      >
        <option value="" disabled>
          {catalog.isLoading
            ? t("common.insurers.loading")
            : remaining.length === 0
              ? t("common.all_selected")
              : placeholderText}
        </option>
        {remaining.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      {value.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {value.map((code) => {
            const label = catalog.labelFor(code);
            return (
              <li
                key={code}
                className="bg-brand-100 text-brand-700 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
              >
                <span>{label}</span>
                <button
                  type="button"
                  onClick={() => onRemove(code)}
                  disabled={disabled}
                  aria-label={t("common.remove", { item: label })}
                  className="text-brand-500 hover:text-brand-800 focus-visible:ring-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:text-slate-300"
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
